import { format, isSameDay } from 'date-fns';
import type { Locale } from 'date-fns';
import { ar, enUS, fr } from 'date-fns/locale';

import type { TimeSlot } from '@klareospace/pharos-lib-types';
import { EMPTY_VALUE_PLACEHOLDER } from '../constants';

const DATE_FNS_LOCALES: Record<string, Locale> = { ar, en: enUS, fr };

export function formatDisplayDate(date: Date): string {
  return format(date, 'PPpp');
}

export function formatShortDate(date: Date): string {
  return format(date, 'dd/MM/yyyy');
}

export function formatShortDateRange(from: Date, to: Date): string {
  return `${formatShortDate(from)} — ${formatShortDate(to)}`;
}

/**
 * Null/invalid-safe short-date formatter.
 * Accepts a Date object, an ISO string, or undefined/null-ish inputs.
 * Returns '—' (em dash) for undefined, null, empty string, or invalid dates.
 */
export function formatShortDateSafe(date: Date | string | undefined): string {
  if (!date) return EMPTY_VALUE_PLACEHOLDER;
  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) return EMPTY_VALUE_PLACEHOLDER;
  return formatShortDate(parsed);
}

/**
 * Local calendar-day key (yyyy-MM-dd), ignoring time-of-day — buckets items by day and doubles
 * as a midnight-rollover-safe memo dependency for callers that group or compare by day.
 */
export function formatDayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function formatEventTimestamp(iso: string): string {
  return format(new Date(iso), 'dd/MM HH:mm');
}

/**
 * Null/invalid-safe variant of {@link formatEventTimestamp}.
 * Accepts a Date object, an ISO string, or undefined/null-ish inputs.
 * Returns `undefined` for undefined, null, empty string, or invalid dates.
 */
export function formatEventTimestampSafe(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined;
  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) return undefined;
  return formatEventTimestamp(parsed.toISOString());
}

/**
 * Null/invalid-safe time-of-day formatter (e.g. a task's `completedAt`).
 * Accepts a Date object, an ISO string, or undefined/null-ish inputs.
 * Returns '—' (em dash) for undefined, null, or invalid dates.
 */
export function formatTimeOnly(date: Date | string | undefined | null): string {
  if (!date) return EMPTY_VALUE_PLACEHOLDER;
  const parsed = date instanceof Date ? date : new Date(date);
  if (isNaN(parsed.getTime())) return EMPTY_VALUE_PLACEHOLDER;
  return format(parsed, 'HH:mm');
}

export function formatTimeWindow(window: TimeSlot): string {
  const start = new Date(window.start);
  const end = new Date(window.end);
  return isSameDay(start, end)
    ? `${format(start, 'dd/MM')} ${format(start, 'HH:mm')}-${format(end, 'HH:mm')}`
    : `${format(start, 'dd/MM')} ${format(start, 'HH:mm')} → ${format(end, 'dd/MM')} ${format(end, 'HH:mm')}`;
}

/**
 * Drawer-only variant of {@link formatTimeWindow}: for a same-day slot, separates the date
 * from the time range with a middle dot (` · `) and uses an en dash (U+2013) between the
 * start and end times — e.g. "19/01 · 10:00–12:00". Multi-day slots keep the same arrow
 * format as `formatTimeWindow`. Do not use this for the orders table — it must keep the
 * existing `formatTimeWindow` output.
 */
export function formatTimeWindowDotted(window: TimeSlot): string {
  const start = new Date(window.start);
  const end = new Date(window.end);
  if (!isSameDay(start, end)) return formatTimeWindow(window);
  return `${format(start, 'dd/MM')} · ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`;
}

/**
 * Formats an inclusive date range as a compact, locale-aware label — e.g. "6 Jul – 10 Jul".
 * Day-of-month + short month; collapses to a single date when `from` and `to` are the same day.
 * Separator is an en dash (U+2013). Unknown locales fall back to English.
 */
export function formatDateRangeLabel(from: Date, to: Date, locale: string = 'en'): string {
  const dfLocale = DATE_FNS_LOCALES[locale] ?? enUS;
  const fmt = (d: Date): string => format(d, 'd MMM', { locale: dfLocale });
  return isSameDay(from, to) ? fmt(from) : `${fmt(from)} – ${fmt(to)}`;
}

/**
 * Format a delivery time slot as a locale-aware date + time range, e.g.
 * "7 Aug · 10:00 – 12:00" (same day) or "7 Aug 10:00 – 8 Aug 12:00" (cross-day).
 * Returns null when the slot is absent. Coerces string inputs to Date so it
 * works with values rehydrated from persisted caches (React Query / AsyncStorage),
 * where start/end may arrive as ISO strings rather than Date objects.
 */
export function formatTimeSlot(timeSlot: TimeSlot | undefined, locale: string = 'en'): string | null {
  if (!timeSlot) return null;
  const dfLocale = DATE_FNS_LOCALES[locale] ?? enUS;
  const start = timeSlot.start instanceof Date ? timeSlot.start : new Date(timeSlot.start);
  const end = timeSlot.end instanceof Date ? timeSlot.end : new Date(timeSlot.end);
  const day = (d: Date): string => format(d, 'd MMM', { locale: dfLocale });
  const time = (d: Date): string => format(d, 'HH:mm');
  if (isSameDay(start, end)) {
    return `${day(start)} · ${time(start)} – ${time(end)}`;
  }
  return `${day(start)} ${time(start)} – ${day(end)} ${time(end)}`;
}
